import { useEffect, useLayoutEffect } from 'react'
import type * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import { useEditor } from '../state/editorStore'
import { useViewStore } from '../state/viewStore'
import { applyView, cameraBasis, fitView } from './camera'
import { isHelper, sceneBoxes } from './helpers'

/**
 * Owns the orthographic camera.
 *
 * The frustum is world-unit and `zoom` stays at 1, so resizing the canvas — or
 * the export squaring it — changes resolution only, never framing.
 */
export function CameraRig() {
  const camera = useThree((state) => state.camera) as THREE.OrthographicCamera
  const scene = useThree((state) => state.scene)
  const width = useThree((state) => state.size.width)
  const height = useThree((state) => state.size.height)
  const invalidate = useThree((state) => state.invalidate)
  const view = useEditor((state) => state.view)
  const fitRequest = useEditor((state) => state.fitRequest)
  const setDirection = useViewStore((state) => state.setDirection)

  useLayoutEffect(() => {
    if (height === 0) return
    applyView(camera, view, width / height)
    // Silhouette extraction depends on the view direction; the store quantises it
    // so this does not rebuild contours on every pixel of an orbit.
    setDirection(cameraBasis(view.mode, view.viewIndex).forward)
    invalidate()
  }, [camera, view, width, height, invalidate, setDirection])

  useEffect(() => {
    if (height === 0) return
    const boxes = sceneBoxes(scene, isHelper)
    const fit = fitView(boxes, width / height, view.mode, view.viewIndex)
    if (fit) useEditor.getState().setView(fit)
    // Intentionally keyed on the fit request only: refitting on every view change
    // would fight the user's panning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitRequest])

  return null
}
